import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import AppDialog from '@/common/components/Dialog.tsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AppDispatch, RootState } from '@/storage/store.ts'
import { useDispatch, useSelector } from 'react-redux'
import { persistor } from '@/storage/store.ts'
import { refreshToken } from '@/storage/user/authSlice.ts'
import { AuthService } from '../authentication/AuthService'

// const WARNING_TIME = 300000
const WARNING_TIME = 300000
export const TimeoutModal = () => {
  const [open, setOpen] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const countdownIntervalRef = useRef<number | null>(null)

  const StoreDispatch = useDispatch<AppDispatch>()
  const AuthSessionState = useSelector((state: RootState) => state.auth)

  const routerLocation = useLocation()
  const excludedRoutes: Array<string> = []
  const timer = useRef<number | null>(null)

  // log out user
  const logoutUser = useCallback(() => {
    stopCountdown()
    setOpen(false)
    AuthService.logout()
    persistor.purge()
  }, [StoreDispatch])

  // session renewal
  const keepWorking = useCallback(() => {
    StoreDispatch(refreshToken())
    setOpen(false)
    stopCountdown()
  }, [StoreDispatch])

  // start counter
  const startCountdown = useCallback(() => {
    setCountdown(WARNING_TIME / 1000)
    countdownIntervalRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          logoutUser()
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [logoutUser])

  // stop counter
  const stopCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
  }

  // format time
  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    const formattedMinutes = String(minutes).padStart(2, '0')
    const formattedSeconds = String(seconds).padStart(2, '0')

    return `${formattedMinutes}:${formattedSeconds}`
  }

  const resetTimer = useCallback(() => {
    stopCountdown()
    setOpen(false)
  }, [stopCountdown])

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current)
    }
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current)
  }

  useEffect(() => {
    const epochNow = Date.now()
    const isTokenExpired = AuthSessionState.expires_at <= epochNow

    if (isTokenExpired) {
      logoutUser()
      return
    }

    const timeToModal = AuthSessionState.expires_at - epochNow - WARNING_TIME
    const timer = window.setTimeout(() => {
      setOpen(true)
      startCountdown()
    }, timeToModal)

    return () => clearTimeout(timer)
  }, [AuthSessionState.expires_at, logoutUser, startCountdown])

  useEffect(() => {
    if (!excludedRoutes.includes(window.location.pathname)) {
      document.addEventListener('click', resetTimer)
      resetTimer()
    }
    return () => {
      clearTimer()
      document.removeEventListener('click', resetTimer)
    }
  }, [routerLocation.pathname])

  return (
    <>
      <AppDialog
        open={open}
        setOpen={() => {
          keepWorking()
          setOpen(false)
        }}
      >
        <Card className="flex flex-col shadow-sm">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-xl font-semibold text-gray-900">
              Session will expire soon!
            </div>
          </CardHeader>
          <CardContent className="py-10">
            <div className="text-center">
              You will be automatically logged out for
              <br /> security reasons in:
            </div>
            <div className="text-center text-3xl font-bold text-primary py-5">
              {formatTime(countdown)}
            </div>
            <div className="text-center">Would you like to keep working?</div>
            {/* <Button className="mt-6">Send</Button> */}
          </CardContent>
          <CardFooter className="flex flex-row gap-2 justify-center">
            <Button variant="outline" onClick={logoutUser} className="px-10">
              Log Out
            </Button>
            <Button onClick={keepWorking} className="px-10">
              Keep Working
            </Button>
          </CardFooter>
        </Card>
      </AppDialog>
    </>
  )
}
